import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Image } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Gallery = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/gallery-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching gallery categories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Default placeholder categories
  const defaultCategories = [
    { 
      id: 'lightboxes-preset', 
      name: 'Lightboxes - Preset Sizes', 
      description: 'Illuminated display solutions in standard sizes',
      image: 'https://customer-assets.emergentagent.com/job_duraplast-mfg/artifacts/imjxfs2o_Alphateta%20and%20Pioneer%20Lightbox.png'
    },
    { id: 'custom-signage', name: 'Custom Signage', description: 'Bespoke signage solutions for businesses', image: '' },
    { id: 'acrylic-fabrication', name: 'Acrylic Fabrication', description: 'Precision-cut acrylic products', image: '' },
    { id: 'point-of-sale', name: 'Point of Sale Displays', description: 'Retail display solutions', image: '' },
    { id: 'vehicle-graphics', name: 'Vehicle Graphics', description: 'Vehicle branding and wraps', image: '' },
    { id: 'dimensional-letters', name: 'Dimensional Letters', description: '3D lettering and signage', image: '' },
    { id: 'event-displays', name: 'Event Displays', description: 'Exhibition and event signage', image: '' },
    { id: 'retail-fixtures', name: 'Retail Fixtures', description: 'Custom retail installations', image: '' },
  ];

  const displayCategories = categories.length > 0 ? categories : defaultCategories;

  if (loading) {
    return <div className="pt-32 px-6 text-center">Loading...</div>;
  }

  return (
    <div className="pt-32 pb-16 px-6 md:px-12 lg:px-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto"
      >
        <h1 className="text-5xl md:text-6xl font-black mb-6" data-testid="gallery-title">GALLERY</h1>
        <p className="text-xl text-white/70 mb-12">Explore our work and craftsmanship</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {displayCategories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              onClick={() => navigate(`/gallery/${category.id}`)}
              className="group overflow-hidden border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-500 cursor-pointer bg-white/5"
              data-testid={`gallery-category-${index}`}
            >
              {/* Title Above Image */}
              <div className="p-4 bg-[#0a0a0a]">
                <h3 className="text-lg font-bold text-white/70 group-hover:text-[#D4AF37] transition-colors">{category.name}</h3>
              </div>
              <div className="w-full h-96 bg-black/20 flex items-center justify-center overflow-hidden">
                {category.image && (
                  <img 
                    src={category.image} 
                    alt=""
                    className="w-full h-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                {!category.image && (
                  <div className="text-white/20 text-center">
                    <Image size={48} />
                  </div>
                )}
              </div>
              <div className="p-4 bg-[#0a0a0a]">
                <p className="text-white/60 text-sm whitespace-pre-line">{category.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Coming Soon Notice */}
        <div className="mt-12 p-6 border border-[#D4AF37]/30 bg-[#D4AF37]/5 rounded-lg text-center">
          <p className="text-[#D4AF37] font-bold mb-1">More Gallery Items Coming Soon</p>
          <p className="text-white/60 text-sm">We're continuously adding new work to our portfolio. Check back regularly for updates!</p>
        </div>
      </motion.div>
    </div>
  );
};

export default Gallery;
